import { z } from "zod/v3";

const BACKUP_CODE_GROUP = 5;
const BACKUP_CODE_LENGTH = 10;

export const INVALID_TOTP_CODE =
  "That code didn't work. Codes refresh every 30 seconds — check your app and try again.";
export const WRONG_PASSWORD = "That password isn't right. Check it and try again.";

export const authenticatorCodeError = (code: string): string | null => {
  if (!code) return "Enter your authenticator code to continue.";
  if (code.length < 6) return `That's only ${code.length} of 6 digits — enter the full code.`;
  return null;
};

export const backupCodeError = (code: string): string | null => {
  const characters = code.replace(/[^A-Za-z0-9]/g, "");
  if (!characters) return "Enter a backup code to continue.";
  if (characters.length < BACKUP_CODE_LENGTH)
    return "Backup codes are 10 characters (XXXXX-XXXXX) — enter the whole code.";
  return null;
};

export const passwordError = (password: string): string | null => (password.trim() ? null : "Enter your password.");

export const formatBackupCode = (input: string): string => {
  const characters = input.replace(/[^A-Za-z0-9]/g, "").slice(0, BACKUP_CODE_LENGTH);
  if (characters.length <= BACKUP_CODE_GROUP) return characters;
  return `${characters.slice(0, BACKUP_CODE_GROUP)}-${characters.slice(BACKUP_CODE_GROUP)}`;
};

export const secretFromTotpUri = (uri: string): string => new URL(uri).searchParams.get("secret") ?? "";

export const groupSecret = (secret: string): string => secret.match(/.{1,4}/g)?.join(" ") ?? secret;

export const backupCodesFile = (codes: string[]) =>
  `Unbox backup codes\n\n${codes.join("\n")}\n\nEach code works once.\n`;

const checkedBy = (check: (value: string) => string | null) =>
  z.string().superRefine((value, ctx) => {
    const message = check(value);
    if (message) ctx.addIssue({ code: "custom", message });
  });

export const passwordSchema = z.object({ password: checkedBy(passwordError) });

export const authenticatorCodeSchema = z.object({ code: checkedBy(authenticatorCodeError) });

export const challengeSchema = z
  .object({ mode: z.enum(["totp", "backup"]), code: z.string(), trustDevice: z.boolean() })
  .superRefine(({ mode, code }, ctx) => {
    const message = mode === "totp" ? authenticatorCodeError(code) : backupCodeError(code);
    if (message) ctx.addIssue({ code: "custom", path: ["code"], message });
  });
